/**
 * Project Requests API
 * Handles employee project allocation requests
 */
import apiClient from './apiClient';

export interface ProjectRequest {
  id: number;
  employee_id: number;
  employee_name: string | null;
  project_id: number;
  project_name: string | null;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  handled_by: number | null;
  handler_name: string | null;
  handled_at: string | null;
  reject_reason: string | null;
  created_at: string;
}

export interface ProjectRequestsListResponse {
  requests: ProjectRequest[];
  total: number;
  page?: number;
  per_page?: number;
  pages?: number;
}

export interface ProjectRequestsQueryParams {
  status?: string;
  page?: number;
  per_page?: number;
}

export const projectRequestsApi = {
  /**
   * Get all project requests (filtered by role)
   */
  getRequests: async (params?: ProjectRequestsQueryParams): Promise<ProjectRequestsListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.per_page) queryParams.append('per_page', String(params.per_page));

    const query = queryParams.toString();
    return apiClient.get<ProjectRequestsListResponse>(`/project-requests${query ? `?${query}` : ''}`);
  },

  /**
   * Get current employee's project requests
   */
  getMyRequests: async (status?: string): Promise<ProjectRequestsListResponse> => {
    const queryParams = new URLSearchParams();
    if (status) queryParams.append('status', status);
    
    const query = queryParams.toString();
    return apiClient.get<ProjectRequestsListResponse>(`/project-requests/my-requests${query ? `?${query}` : ''}`);
  },

  /**
   * Get pending project requests (PMO/SuperAdmin only)
   */
  getPendingRequests: async (): Promise<ProjectRequestsListResponse> => {
    return apiClient.get<ProjectRequestsListResponse>('/project-requests/pending');
  },

  /**
   * Get a specific project request
   */
  getRequest: async (requestId: number): Promise<{ request: ProjectRequest }> => {
    return apiClient.get<{ request: ProjectRequest }>(`/project-requests/${requestId}`);
  },

  /**
   * Create a new project request
   */
  createRequest: async (data: {
    project_id: number;
    reason?: string;
  }): Promise<{ message: string; request: ProjectRequest }> => {
    return apiClient.post('/project-requests', data);
  },

  /**
   * Approve a project request (PMO/SuperAdmin only)
   */
  approveRequest: async (requestId: number): Promise<{ message: string; request: ProjectRequest }> => {
    return apiClient.put(`/project-requests/${requestId}/approve`);
  },

  /**
   * Reject a project request (PMO/SuperAdmin only)
   */
  rejectRequest: async (requestId: number, reason?: string): Promise<{ message: string; request: ProjectRequest }> => {
    return apiClient.put(`/project-requests/${requestId}/reject`, { reason });
  },

  /**
   * Cancel own pending request
   */
  cancelRequest: async (requestId: number): Promise<{ message: string; request: ProjectRequest }> => {
    return apiClient.put(`/project-requests/${requestId}/cancel`);
  },
};

export default projectRequestsApi;
